import { Box, FormControlLabel, Grid, Switch } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import {
  useGetMeasureUnitsQuery,
  useGetTemperatureRangesQuery,
} from '../../../../../app/redux-fetch/apiCoreQuery'
import { convertToLabelValue } from '../../../../../app/utils'
import DefaultSpacingGrid from '../../../../../components/Containers/DefaultSpacingGrid'
import CombinedSelect from '../../../../../components/Forms/CombinedSelect'
import FormInput from '../../../../../components/Forms/FormInput'
import FormSelect from '../../../../../components/Forms/FormSelect'
import InfoDisplayItem from '../../../../../components/Typographies/InfoDisplayItem'
import { useOrderFormContext } from '../../../context/OrderFormContext'
import messages from '../../messages'

interface TemperatureTabProps {
  prefix?: string
  disabled?: boolean
}

const TemperatureTab: React.FC<TemperatureTabProps> = ({ prefix = '', disabled = false }) => {
  const { watch, setValue, control, unregister, trigger } = useFormContext()
  const { formatMessage } = useIntl()
  const { variant } = useOrderFormContext()

  const { data: measureUnits = [] } = useGetMeasureUnitsQuery()

  const { data: temperatureRange = [] } = useGetTemperatureRangesQuery()

  const temperatureUnitsOptions = useMemo(() => {
    return convertToLabelValue(
      measureUnits
        .filter((x) => x.measureTypeId === 3)
        .map((x) => {
          return { name: x.units, id: x.id }
        })
    )
  }, [measureUnits])

  const temperatureRangeOptions = useMemo(() => {
    return convertToLabelValue(temperatureRange)
  }, [temperatureRange])

  const unit = watch(`${prefix}.temperatureSetting.temperatureUnitId`)
  const temperatureControlled = watch(`${prefix}.temperatureControlled`)

  const getTemperatureUnit = useCallback(
    (id: number) => {
      return measureUnits.find((unit) => unit.id === id)?.units
    },
    [measureUnits]
  )

  const handleTemperatureControlledChange = (checked: boolean) => {
    setValue(`${prefix}.temperatureControlled`, checked)
    if (checked) {
      setValue(`${prefix}.temperatureSetting`, {
        setPoint: null,
        temperatureRangeId: undefined,
        temperatureUnitId: null,
        upperThresholdWarning: null,
        upperThresholdCritical: null,
        lowerThresholdWarning: null,
        lowerThresholdCritical: null,
      })
    } else {
      unregister(`${prefix}.temperatureSetting`)
    }
  }

  const handleInputChange = async () => {
    await trigger(`${prefix}.temperatureSetting.upperThresholdWarning`)
    await trigger(`${prefix}.temperatureSetting.upperThresholdCritical`)
    await trigger(`${prefix}.temperatureSetting.lowerThresholdWarning`)
    await trigger(`${prefix}.temperatureSetting.lowerThresholdCritical`)
    await trigger(`${prefix}.temperatureSetting.setPoint`)
  }

  if (variant === 'summary') {
    const temperatureUnit = getTemperatureUnit(unit)
    const appendUnit = (val: any) => {
      if (val === undefined || val === null || val === '' || val === 'N/A') return 'N/A'
      return temperatureUnit ? `${val} ${temperatureUnit}` : val
    }
    return (
      <Box sx={{ padding: '22px 16px 8px 16px' }}>
        <DefaultSpacingGrid container>
          <Grid item xs={12} md={4}>
            <InfoDisplayItem
              label={formatMessage(messages.temperatureRange)}
              value={
                temperatureRangeOptions.find(
                  (opt) => opt.value === watch(`${prefix}.temperatureSetting.temperatureRangeId`)
                )?.label || 'N/A'
              }
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <InfoDisplayItem
              label={formatMessage(messages.temperatureSetPoint)}
              value={appendUnit(watch(`${prefix}.temperatureSetting.setPoint`))}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem
              label={formatMessage(messages.lowerCritical)}
              value={appendUnit(watch(`${prefix}.temperatureSetting.lowerThresholdCritical`))}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem
              label={formatMessage(messages.lowerWarning)}
              value={appendUnit(watch(`${prefix}.temperatureSetting.lowerThresholdWarning`))}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem
              label={formatMessage(messages.upperWarning)}
              value={appendUnit(watch(`${prefix}.temperatureSetting.upperThresholdWarning`))}
              isSmallScreen={false}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <InfoDisplayItem
              label={formatMessage(messages.upperCritical)}
              value={appendUnit(watch(`${prefix}.temperatureSetting.upperThresholdCritical`))}
              isSmallScreen={false}
            />
          </Grid>
        </DefaultSpacingGrid>
      </Box>
    )
  }

  return (
    <Box sx={{ padding: '22px 16px 0 16px' }}>
      <DefaultSpacingGrid container>
        <Grid item xs={12} md={12}>
          <FormControlLabel
            control={
              <Switch
                checked={!!temperatureControlled}
                onChange={(e) => handleTemperatureControlledChange(e.target.checked)}
                disabled={disabled}
              />
            }
            label={formatMessage(messages.temperatureControlled)}
          />
        </Grid>
        {!!temperatureControlled && (
          <>
            <Grid item xs={12} md={2}>
              <FormSelect
                name={`${prefix}.temperatureSetting.temperatureRangeId`}
                label={formatMessage(messages.temperatureRange)}
                options={temperatureRangeOptions}
                required={true}
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <CombinedSelect
                nameValue={`${prefix}.temperatureSetting.setPoint`}
                nameUnit={`${prefix}.temperatureSetting.temperatureUnitId`}
                labelValue={formatMessage(messages.temperatureSetPoint)}
                labelUnitValue={formatMessage(messages.unitLabel)}
                optionsUnit={temperatureUnitsOptions}
                required={true}
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                  allowNegative: true,
                }}
                maxValue={9999.9999}
                disabled={disabled}
                onChange={(e) => handleInputChange()}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormInput
                name={`${prefix}.temperatureSetting.lowerThresholdCritical`}
                label={formatMessage(messages.lowerCritical)}
                InputLabelProps={{ shrink: true }}
                type='number'
                unit={getTemperatureUnit(unit)}
                required={true}
                fullWidth
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                  allowNegative: true,
                }}
                maxValue={9999.9999}
                disabled={disabled}
                onChange={(e) => handleInputChange()}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormInput
                name={`${prefix}.temperatureSetting.lowerThresholdWarning`}
                label={formatMessage(messages.lowerWarning)}
                InputLabelProps={{ shrink: true }}
                type='number'
                maxValue={9999.9999}
                unit={getTemperatureUnit(unit)}
                fullWidth
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                  allowNegative: true,
                }}
                disabled={disabled}
                onChange={(e) => handleInputChange()}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormInput
                name={`${prefix}.temperatureSetting.upperThresholdWarning`}
                label={formatMessage(messages.upperWarning)}
                InputLabelProps={{ shrink: true }}
                type='number'
                maxValue={9999.9999}
                unit={getTemperatureUnit(unit)}
                fullWidth
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                  allowNegative: true,
                }}
                disabled={disabled}
                onChange={(e) => handleInputChange()}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormInput
                name={`${prefix}.temperatureSetting.upperThresholdCritical`}
                label={formatMessage(messages.upperCritical)}
                InputLabelProps={{ shrink: true }}
                type='number'
                maxValue={9999.9999}
                unit={getTemperatureUnit(unit)}
                required={true}
                fullWidth
                numberFormatProps={{
                  decimalScale: 4,
                  fixedDecimalScale: true,
                  allowNegative: true,
                }}
                disabled={disabled}
                onChange={(e) => handleInputChange()}
              />
            </Grid>
          </>
        )}
      </DefaultSpacingGrid>
    </Box>
  )
}

export default TemperatureTab
